import { useEffect, useRef, useState } from 'react';
import { createChart, CandlestickSeries, LineSeries, createSeriesMarkers } from 'lightweight-charts';
import { getBotCandles, getBotMarkers, getBotLevels } from './api';

const s = {
  dark:   '#0a0a0a',
  card:   '#111111',
  border: '#222222',
  green:  '#00ff88',
  red:    '#ff4444',
  muted:  '#888888',
  white:  '#ffffff',
};

const levelColors = {
  NYH:'#f97316', NYL:'#f97316',
  AsiaH:'#a855f7', AsiaL:'#a855f7',
  LondonH:'#3b82f6', LondonL:'#3b82f6',
  GlobexH:'#14b8a6', GlobexL:'#14b8a6',
  WeekH:'#eab308', WeekL:'#eab308',
  MonthH:'#ef4444', MonthL:'#ef4444',
};

export default function Chart({ botId, wsLogs }) {
  const containerRef = useRef(null);
  const chartRef     = useRef(null);
  const candleRef    = useRef(null);
  const linesRef     = useRef([]);
  const [tf, setTf]  = useState('5M');
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const initChart = () => {
    if (!containerRef.current) return;
    if (chartRef.current) { chartRef.current.remove(); chartRef.current = null; }

    const chart = createChart(containerRef.current, {
      width:  containerRef.current.clientWidth || 800,
      height: 450,
      layout: {
        background: { color: s.card },
        textColor:  s.muted,
      },
      grid: {
        vertLines: { color: s.border },
        horzLines: { color: s.border },
      },
      crosshair: {
        vertLine: { color: s.muted },
        horzLine: { color: s.muted },
      },
      rightPriceScale: { borderColor: s.border },
      timeScale: {
        borderColor:    s.border,
        timeVisible:    true,
        secondsVisible: false,
      },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {
      upColor:         s.green,
      downColor:       s.red,
      borderUpColor:   s.green,
      borderDownColor: s.red,
      wickUpColor:     s.green,
      wickDownColor:   s.red,
    });

    chartRef.current  = chart;
    candleRef.current = candleSeries;

    const ro = new ResizeObserver(() => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: containerRef.current.clientWidth });
      }
    });
    ro.observe(containerRef.current);
    return () => { ro.disconnect(); if (chartRef.current) chartRef.current.remove(); };
  };

  const loadData = async () => {
    if (!botId || !candleRef.current) return;
    setLoading(true);
    setError('');
    try {
      const [candles, markers, levels] = await Promise.all([
        getBotCandles(botId, tf, 150),
        getBotMarkers(botId),
        getBotLevels(botId),
      ]);

      if (!candleRef.current) return;

      if (candles.length > 0) {
        candleRef.current.setData(candles);
      }

      // Remove old price lines
      linesRef.current.forEach(l => { try { candleRef.current.removePriceLine(l); } catch {} });
      linesRef.current = [];

      // Session level lines
      levels.forEach(lv => {
        const color = levelColors[lv.type] || s.muted;
        const line = candleRef.current.createPriceLine({
          price:            lv.price,
          color:            color,
          lineWidth:        1,
          lineStyle:        2,
          axisLabelVisible: true,
          title:            lv.type,
        });
        linesRef.current.push(line);
      });

      // Trade markers
      if (markers.length > 0) {
        const chartMarkers = markers.map(m => {
          if (m.type === 'entry') {
            return {
              time:     m.time,
              position: m.direction === 'LONG' ? 'belowBar' : 'aboveBar',
              color:    m.direction === 'LONG' ? s.green : s.red,
              shape:    m.direction === 'LONG' ? 'arrowUp' : 'arrowDown',
              text:     `${m.direction} @ ${m.price}`,
            };
          } else {
            const win = m.pnl_pts > 0;
            return {
              time:     m.time,
              position: m.direction === 'LONG' ? 'aboveBar' : 'belowBar',
              color:    win ? s.green : s.red,
              shape:    'square',
              text:     `Exit ${m.pnl_pts > 0 ? '+' : ''}${m.pnl_pts}pt ($${m.pnl_usd})`,
            };
          }
        });
        createSeriesMarkers(candleRef.current, chartMarkers);
      }

    } catch(e) {
      setError('Failed to load chart data: ' + e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    const cleanup = initChart();
    loadData();
    return cleanup;
  }, []);

  useEffect(() => {
    if (chartRef.current && candleRef.current) loadData();
  }, [botId, tf]);

  // Update on new candle close from WebSocket
  useEffect(() => {
    if (!wsLogs || wsLogs.length === 0 || !candleRef.current) return;
    const lastLine = wsLogs[wsLogs.length - 1];
    const pat = new RegExp(`CandleClose \\[${tf}\\]`);
    if (pat.test(lastLine)) {
      getBotCandles(botId, tf, 150).then(candles => {
        if (candleRef.current && candles.length > 0) {
          candleRef.current.setData(candles);
        }
      }).catch(() => {});
    }
  }, [wsLogs]);

  return (
    <div style={{background:s.card,border:`1px solid ${s.border}`,borderRadius:'8px',padding:'16px'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'12px'}}>
        <p style={{color:s.muted,fontSize:'0.7rem',textTransform:'uppercase',letterSpacing:'0.05em',margin:0}}>
          Live Chart
        </p>
        <div style={{display:'flex',gap:'4px'}}>
          {['1M','5M'].map(t => (
            <button key={t} onClick={() => setTf(t)}
              style={{padding:'2px 10px',
                background:tf===t?s.green+'22':'transparent',
                border:`1px solid ${tf===t?s.green:s.border}`,
                color:tf===t?s.green:s.muted,
                borderRadius:'4px',cursor:'pointer',fontSize:'0.75rem'}}>
              {t}
            </button>
          ))}
          <button onClick={loadData}
            style={{padding:'2px 10px',background:'transparent',
              border:`1px solid ${s.border}`,color:s.muted,
              borderRadius:'4px',cursor:'pointer',fontSize:'0.75rem'}}>
            ↻
          </button>
        </div>
      </div>

      <div style={{display:'flex',gap:'16px',marginBottom:'8px',flexWrap:'wrap'}}>
        <span style={{color:'#f97316',fontSize:'0.65rem'}}>— NYH/NYL</span>
        <span style={{color:'#a855f7',fontSize:'0.65rem'}}>— Asia</span>
        <span style={{color:'#3b82f6',fontSize:'0.65rem'}}>— London</span>
        <span style={{color:'#14b8a6',fontSize:'0.65rem'}}>— Globex</span>
        <span style={{color:s.green,fontSize:'0.65rem'}}>▲ Long Entry</span>
        <span style={{color:s.red,fontSize:'0.65rem'}}>▼ Short Entry</span>
        <span style={{color:s.muted,fontSize:'0.65rem'}}>■ Exit</span>
      </div>

      {loading && (
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'450px'}}>
          <span style={{color:s.green}}>Loading chart...</span>
        </div>
      )}
      {error && (
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'450px',flexDirection:'column',gap:'8px'}}>
          <span style={{color:s.red}}>{error}</span>
          <button onClick={loadData}
            style={{padding:'4px 12px',background:'transparent',border:`1px solid ${s.border}`,
              color:s.muted,borderRadius:'4px',cursor:'pointer',fontSize:'0.75rem'}}>
            Retry
          </button>
        </div>
      )}
      <div ref={containerRef} style={{display:loading||error?'none':'block'}} />
    </div>
  );
}
